import { PathLayer } from '@deck.gl/layers';
import Flatbush from 'flatbush';

type BinaryPathData = {
  length: number;
  startIndices: ArrayLike<number>;
  attributes?: {
    getPath?: {
      value: ArrayLike<number>;
      size: number;
    };
  };
};

type MapLasPathLayerProps = Omit<ConstructorParameters<typeof PathLayer>[0], 'data'> & {
  data?: BinaryPathData;
  filterRange?: readonly [number, number];
};

type Segment = {
  start: number;
  end: number;
  lineIndex: number;
};

export class MapLasPathLayer {
  private props: MapLasPathLayerProps;
  private layer: PathLayer;
  private data: BinaryPathData | undefined;
  private readonly segments: Segment[] = [];
  readonly spatialIndex: Flatbush | null;

  constructor(props: MapLasPathLayerProps) {
    this.props = props;
    this.layer = new PathLayer(props as ConstructorParameters<typeof PathLayer>[0]);
    this.data = props.data;
    this.spatialIndex = this.createSpatialIndex(this.data);
  }

  getLayer(): PathLayer {
    return this.layer;
  }

  setVisible(visible: boolean): void {
    if (this.props.visible === visible) return;
    this.props = { ...this.props, visible };
    this.layer = new PathLayer(this.props as ConstructorParameters<typeof PathLayer>[0]);
  }

  setFilterRange(filterRange: readonly [number, number]): void {
    if (this.props.filterRange?.[0] === filterRange[0] && this.props.filterRange?.[1] === filterRange[1]) return;
    this.props = { ...this.props, filterRange };
    this.layer = new PathLayer(this.props as ConstructorParameters<typeof PathLayer>[0]);
  }

  pick(lat: number, lon: number): number | null {
    const positions = this.data?.attributes?.getPath;
    const starts = this.data?.startIndices;
    const map = typeof window === 'undefined' ? null : window.mainmap;
    const width = this.layer.props.getWidth;
    if (!this.spatialIndex || !positions || !starts || !map || typeof width !== 'number'
      || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    const queryPoint = map.latLngToContainerPoint([lat, lon]);
    const radius = width;
    const northWest = map.containerPointToLatLng([queryPoint.x - radius, queryPoint.y - radius]);
    const southEast = map.containerPointToLatLng([queryPoint.x + radius, queryPoint.y + radius]);
    const candidates = this.spatialIndex.search(
      Math.min(northWest.lng, southEast.lng), Math.min(northWest.lat, southEast.lat),
      Math.max(northWest.lng, southEast.lng), Math.max(northWest.lat, southEast.lat),
    );

    let closestLine = -1;
    let closestDistanceSquared = Infinity;
    for (const segmentIndex of candidates) {
      const segment = this.segments[segmentIndex];
      const startOffset = segment.start * positions.size;
      const endOffset = segment.end * positions.size;
      const startPoint = map.latLngToContainerPoint([positions.value[startOffset + 1], positions.value[startOffset]]);
      const endPoint = map.latLngToContainerPoint([positions.value[endOffset + 1], positions.value[endOffset]]);
      const dx = endPoint.x - startPoint.x;
      const dy = endPoint.y - startPoint.y;
      const lengthSquared = dx * dx + dy * dy;
      const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
        ((queryPoint.x - startPoint.x) * dx + (queryPoint.y - startPoint.y) * dy) / lengthSquared));
      const distanceX = queryPoint.x - (startPoint.x + t * dx);
      const distanceY = queryPoint.y - (startPoint.y + t * dy);
      const distanceSquared = distanceX * distanceX + distanceY * distanceY;
      if (distanceSquared < closestDistanceSquared) {
        closestDistanceSquared = distanceSquared;
        closestLine = segment.lineIndex;
      }
    }

    return closestLine !== -1 && closestDistanceSquared <= radius * radius ? closestLine : null;
  }

  private createSpatialIndex(data: BinaryPathData | undefined): Flatbush | null {
    const positions = data?.attributes?.getPath;
    const starts = data?.startIndices;
    if (!data || !positions || positions.size < 2 || !starts || data.length === 0) return null;

    let segmentCount = 0;
    for (let lineIndex = 0; lineIndex < data.length; lineIndex += 1) {
      const start = starts[lineIndex];
      const end = starts[lineIndex + 1] ?? Math.floor(positions.value.length / positions.size);
      segmentCount += Math.max(0, end - start - 1);
    }
    if (segmentCount === 0) return null;

    const index = new Flatbush(segmentCount);
    for (let lineIndex = 0; lineIndex < data.length; lineIndex += 1) {
      const start = starts[lineIndex];
      const end = starts[lineIndex + 1] ?? Math.floor(positions.value.length / positions.size);
      for (let pointIndex = start; pointIndex + 1 < end; pointIndex += 1) {
        const firstOffset = pointIndex * positions.size;
        const secondOffset = (pointIndex + 1) * positions.size;
        const firstLon = positions.value[firstOffset];
        const firstLat = positions.value[firstOffset + 1];
        const secondLon = positions.value[secondOffset];
        const secondLat = positions.value[secondOffset + 1];
        this.segments.push({ start: pointIndex, end: pointIndex + 1, lineIndex });
        index.add(Math.min(firstLon, secondLon), Math.min(firstLat, secondLat),
          Math.max(firstLon, secondLon), Math.max(firstLat, secondLat));
      }
    }
    index.finish();
    return index;
  }
}
